import React, { useCallback, useRef } from 'react';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';
import { DAY_W } from './grid-constants';

interface Props {
  vehicleId: string;
  dateStr: string;
  /** px offset within the lane (dayIndex * DAY_W). */
  left: number;
  onCellClick: (vehicleId: string, dateStr: string, rect: DOMRect) => void;
}

/**
 * One day in a vehicle lane. This is the lightweight pointer/click target layer
 * that sits BEHIND the run-level band blocks:
 *   - click an empty day → assign popover (run blocks above handle their own
 *     clicks and stop propagation)
 *   - it carries data-date / data-vehicle-id so the global DragController's
 *     elementFromPoint can map the pointer to a (vehicle, day) while extending
 *   - it renders the per-day invalid-target rail during a drag (the band block
 *     itself drives the live extend/shrink preview)
 */
const VehicleScheduleCell: React.FC<Props> = React.memo(({
  vehicleId, dateStr, left, onCellClick,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);

  const drag = useVehicleScheduleStore(s => (s.drag && s.drag.vehicleId === vehicleId ? s.drag : null));

  const handleCellClick = useCallback(() => {
    const rect = cellRef.current?.getBoundingClientRect();
    if (rect) onCellClick(vehicleId, dateStr, rect);
  }, [vehicleId, dateStr, onCellClick]);

  return (
    <div
      ref={cellRef}
      role="gridcell"
      tabIndex={0}
      aria-label={dateStr}
      data-date={dateStr}
      data-vehicle-id={vehicleId}
      className="absolute top-0 h-full cursor-pointer outline-none focus:ring-1 focus:ring-inset focus:ring-primary/30 hover:bg-gray-700/15 day:hover:bg-gray-200/60 transition-colors border-r border-gray-800/40 day:border-gray-200"
      style={{ left, width: DAY_W }}
      onClick={handleCellClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCellClick(); } }}
    >
      {drag && drag.previewDate === dateStr && drag.invalid && (
        <div className="absolute inset-0 pointer-events-none ring-2 ring-inset ring-red-500/70 rounded" />
      )}
    </div>
  );
});

VehicleScheduleCell.displayName = 'VehicleScheduleCell';
export default VehicleScheduleCell;
